import {
    parseMagicMd,
    type ForBlock,
    type ParsedDocument,
    type PluginDefinition,
    type PluginOutput,
} from "@magic-md/core";
import type {
    SessionGetStateResult,
} from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import type { ActionResult, ApiResult } from "@magic-md/plugin-sdk";
import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { buildDocumentRenderContext } from "../lib/document-render-context";
import {
    createWorkspacePinnedUnit,
    findWorkspacePinnedUnit,
    type WorkspacePinnedUnit,
} from "../lib/pinned-units";
import { pickerInitialPathFromDocument } from "../lib/path-utils";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { getPlatformService } from "../platform";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import {
    applyHydratedUnitOutputToExecutionMaps,
    type ExecutionMapSet,
} from "../store/execution-entry-builders";
import { useEditorStore } from "../store/editor-store";
import {
    ensurePinnedUnitSession,
    pinnedUnitSessionRegistry,
} from "../store/pinned-unit-sessions";
import type {
    ContentExecutionEntry,
    ExecutionEntry,
    ForExecutionEntry,
    ImportExecutionEntry,
    UseExecutionEntry,
} from "../store/plugin-execution-store";
import { getNotificationBus } from "../store/notification-bus";
import { cn } from "../lib/utils";
import { showFilePickerDialog, showFolderPickerDialog } from "./dialogs";
import { PluginBlock } from "./document-renderer/blocks/PluginBlock";
import type { DocContextValue } from "./document-renderer/contexts/DocContext";
import { DocContextProvider } from "./document-renderer/contexts/DocContext";
import { ExecutionContextProvider } from "./document-renderer/contexts/ExecutionContext";
import { PluginErrorBoundary } from "./PluginErrorBoundary";

type PinnedSnapshot = {
    sessionId: string;
    sourceContent: string;
    definitions: Map<string, PluginDefinition>;
    outputs: Map<string, PluginOutput>;
    executions: Map<string, ExecutionEntry>;
    forExecutions: Map<string, ForExecutionEntry>;
    contentExecutions: Map<string, ContentExecutionEntry>;
    importExecutions: Map<string, ImportExecutionEntry>;
    useExecutions: Map<string, UseExecutionEntry>;
    contentDefinitions: ReturnType<typeof buildDocumentRenderContext>["contentDefinitions"];
    forBlocksMap: Map<string, ForBlock>;
    importDocuments: ReturnType<typeof buildDocumentRenderContext>["importDocuments"];
    useBindings: ReturnType<typeof collectParsedDocumentUseBindings>;
};

function createEmptyExecutionMaps(): ExecutionMapSet {
    return {
        executions: new Map(),
        forExecutions: new Map(),
        contentExecutions: new Map(),
        importExecutions: new Map(),
        useExecutions: new Map(),
    };
}

async function readPinnedDocumentContent(documentPath: string): Promise<string> {
    const openTab = useEditorStore
        .getState()
        .tabs.find((tab) => tab.filePath === documentPath);
    if (openTab) return openTab.content;
    return (await getPlatformService().engine.workspaceReadFile({
        path: documentPath,
    })).content;
}

function buildEngineDefinitions(
    units: SessionGetStateResult["units"],
): Map<string, PluginDefinition> {
    const definitions = new Map<string, PluginDefinition>();
    for (const unit of units) {
        if (unit.unitType !== "definition") continue;
        definitions.set(unit.id, {
            id: unit.id,
            pluginName: unit.pluginName,
            config: unit.config,
            template: unit.isTemplate ?? false,
            params: unit.params,
        });
    }
    return definitions;
}

function buildForBlocksMap(parsedDocument: ParsedDocument): Map<string, ForBlock> {
    const map = new Map<string, ForBlock>();
    for (const block of parsedDocument.blocks) {
        if (block.type !== "for-block") continue;
        const forDefinition = parsedDocument.forDefinitions?.get(block.id);
        map.set(block.id, {
            ...block,
            template: forDefinition?.template ?? block.template,
            params: forDefinition?.params ?? block.params,
            externalRef: forDefinition?.externalRef ?? block.externalRef,
        });
    }
    return map;
}

async function buildPinnedSnapshot(unit: WorkspacePinnedUnit): Promise<PinnedSnapshot> {
    const engine = getPlatformService().engine;
    const sessionId = await ensurePinnedUnitSession(unit);
    const sourceContent = await readPinnedDocumentContent(unit.documentPath);
    await engine.sessionSync({
        sessionId,
        content: sourceContent,
        includeOutputs: false,
    });
    const parsedDocument = parseMagicMd(sourceContent);
    const sessionState = await engine.sessionGetState({ sessionId });
    pinnedUnitSessionRegistry.attachSession(unit.id, sessionId);

    const executionMaps = createEmptyExecutionMaps();
    for (const [unitId, output] of Object.entries(sessionState.outputs)) {
        applyHydratedUnitOutputToExecutionMaps(
            executionMaps,
            unitId,
            output,
            { mode: "always" },
        );
    }

    const renderContext = buildDocumentRenderContext({
        parsedDocument,
        executions: executionMaps.executions,
        contentExecutions: executionMaps.contentExecutions,
        importExecutions: executionMaps.importExecutions,
        useExecutions: executionMaps.useExecutions,
        engineDefinitions: buildEngineDefinitions(sessionState.units),
    });

    return {
        sessionId,
        sourceContent,
        definitions: renderContext.definitions,
        outputs: renderContext.outputs,
        executions: executionMaps.executions,
        forExecutions: executionMaps.forExecutions,
        contentExecutions: executionMaps.contentExecutions,
        importExecutions: executionMaps.importExecutions,
        useExecutions: executionMaps.useExecutions,
        contentDefinitions: renderContext.contentDefinitions,
        forBlocksMap: buildForBlocksMap(parsedDocument),
        importDocuments: renderContext.importDocuments,
        useBindings: collectParsedDocumentUseBindings(parsedDocument),
    };
}

async function waitForExecution(executionId: string) {
    const engine = getPlatformService().engine;
    for (;;) {
        const state = await engine.executionGetState({ executionId });
        if (state.status !== "running") return state;
        await new Promise((resolve) => setTimeout(resolve, 250));
    }
}

export function PinnedUnitPanel({
    unit,
    className,
}: {
    unit: WorkspacePinnedUnit;
    className?: string;
}) {
    const t = useT("editor");
    const [snapshot, setSnapshot] = useState<PinnedSnapshot | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [runningUnitId, setRunningUnitId] = useState<string | null>(null);
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
    const addPinnedUnit = useEditorStore((s) => s.addPinnedUnit);
    const removePinnedUnit = useEditorStore((s) => s.removePinnedUnit);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setSnapshot(await buildPinnedSnapshot(unit));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [unit]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        buildPinnedSnapshot(unit)
            .then((next) => {
                if (!cancelled) setSnapshot(next);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [unit]);

    const runUnit = useCallback(
        async (
            unitId: string,
            action?: string,
            actionParams?: Record<string, unknown>,
        ): Promise<ActionResult | void> => {
            const sessionId = snapshot?.sessionId ?? await ensurePinnedUnitSession(unit);
            setRunningUnitId(unitId);
            try {
                const start = await getPlatformService().engine.executionStart({
                    sessionId,
                    target: "unit",
                    unitId,
                    action,
                    actionParams,
                });
                const finalState = await waitForExecution(start.executionId);
                await refresh();
                if (finalState.status === "completed") {
                    const output =
                        finalState.output?.unitType === "definition"
                            ? finalState.output.data
                            : undefined;
                    return { success: true, output };
                }
                return {
                    success: false,
                    message: finalState.message ?? finalState.error ?? "Execution failed",
                };
            } finally {
                setRunningUnitId(null);
            }
        },
        [refresh, snapshot?.sessionId, unit],
    );

    const pickerInitialPath = pickerInitialPathFromDocument(unit.documentPath);
    const documentBundlePath = getDocumentBundleDir(unit.documentPath);
    const pinnedUnitIds = useMemo(() => new Set([unit.unitId]), [unit.unitId]);

    const docValue = useMemo<DocContextValue | null>(() => {
        if (!snapshot) return null;
        return {
            definitions: snapshot.definitions,
            outputs: snapshot.outputs,
            registry: pluginUIRegistry,
            executable: true,
            documentPath: unit.documentPath,
            sourceContent: snapshot.sourceContent,
            getSourceContent: () => snapshot.sourceContent,
            documentBundlePath,
            contentDefinitions: snapshot.contentDefinitions,
            forBlocksMap: snapshot.forBlocksMap,
            useBindings: snapshot.useBindings,
            importDocuments: snapshot.importDocuments,
            onRun: (definitionId) => {
                void runUnit(definitionId);
            },
            onAction: (definitionId, actionName, params) =>
                runUnit(definitionId, actionName, params),
            onApi: async <TData = unknown,>(
                definitionId: string,
                apiName: string,
                params?: Record<string, unknown>,
            ): Promise<ApiResult<TData>> => {
                const engine = getPlatformService().engine;
                if (!engine.pluginApiCall) {
                    return {
                        success: false,
                        message: "Plugin runtime API is not supported by this engine",
                    };
                }
                const sessionId =
                    snapshot.sessionId ?? await ensurePinnedUnitSession(unit);
                return engine.pluginApiCall<TData>({
                    sessionId,
                    unitId: definitionId,
                    api: apiName,
                    params,
                });
            },
            onPin: (definitionId) => {
                const definition = snapshot.definitions.get(definitionId);
                if (!definition) return;
                const existing = findWorkspacePinnedUnit(
                    useEditorStore.getState().pinnedUnits,
                    unit.documentPath,
                    definitionId,
                );
                if (existing) {
                    removePinnedUnit(existing.id);
                    return;
                }
                addPinnedUnit(
                    createWorkspacePinnedUnit(unit.documentPath, definition),
                    { sessionId: snapshot.sessionId },
                );
            },
            onDefinitionSelect: (definitionId) => {
                void openFileFromPath(unit.documentPath).then(() => {
                    const editor = useEditorStore.getState();
                    editor.setRightSidebarDefinitionId(definitionId);
                    if (!editor.rightSidebarOpen) editor.toggleRightSidebar();
                });
            },
            onLoadOutput: async () => {
                await refresh();
            },
            callSidecar: (sidecarId, method, params) =>
                getPlatformService().engine.sidecarCall(sidecarId, method, params),
            onEngineNotification: <TData = unknown,>(
                method: string,
                handler: (params: TData) => void,
            ) => getNotificationBus().subscribe<TData>(method, handler),
            selectFile: (options) =>
                showFilePickerDialog({
                    accept: options?.accept,
                    title: options?.title,
                    initialPath: pickerInitialPath,
                }),
            selectFolder: (options) =>
                showFolderPickerDialog({
                    title: options?.title,
                    initialPath: pickerInitialPath,
                }),
            openFile: (filePath) => openFileFromPath(filePath),
            openExternal: (url) =>
                getPlatformService().openExternal?.(url) ?? Promise.resolve(),
            getAssetUrl: (filePath) => getPlatformService().getAssetUrl?.(filePath),
            getDefinitions: () =>
                Array.from(snapshot.definitions.values()).map((definition) => ({
                    id: definition.id,
                    pluginName: definition.pluginName,
                    hasOutput: snapshot.outputs.has(definition.id),
                })),
            getTabs: () =>
                useEditorStore.getState().tabs.map((tab) => ({
                    id: tab.id,
                    title: tab.title,
                    filePath: tab.filePath,
                })),
        };
    }, [
        addPinnedUnit,
        documentBundlePath,
        openFileFromPath,
        pickerInitialPath,
        refresh,
        removePinnedUnit,
        runUnit,
        snapshot,
        unit,
    ]);

    const executionValue = useMemo(() => {
        if (!snapshot) return null;
        const executionStatuses = new Map(
            Array.from(snapshot.executions, ([id, entry]) => [id, entry.status]),
        );
        if (runningUnitId) executionStatuses.set(runningUnitId, "running");
        return {
            pinnedDefinitions: pinnedUnitIds,
            runningDefinitions: runningUnitId ? new Set([runningUnitId]) : new Set<string>(),
            executionStatuses,
            executionDurations: new Map(
                Array.from(snapshot.executions)
                    .filter(([, entry]) => entry.duration != null)
                    .map(([id, entry]) => [id, entry.duration as number]),
            ),
            forExecutions: snapshot.forExecutions,
            contentExecutions: snapshot.contentExecutions,
            importExecutions: snapshot.importExecutions,
            useExecutions: snapshot.useExecutions,
            sessionConnected: true,
        };
    }, [pinnedUnitIds, runningUnitId, snapshot]);

    if (loading && !snapshot) {
        return (
            <div className={cn("flex h-full items-center justify-center p-4 text-xs text-muted-foreground", className)}>
                <Loader2 size={14} className="mr-2 animate-spin" />
                {t("sidebar.pinnedUnit.loading")}
            </div>
        );
    }

    if (error || !snapshot || !docValue || !executionValue) {
        return (
            <div className={cn("flex h-full items-center justify-center p-4", className)}>
                <div className="max-w-[240px] text-center">
                    <div className="text-xs font-medium text-foreground">
                        {t("sidebar.pinnedUnit.unavailable")}
                    </div>
                    <div className="mt-1 break-words text-[11px] leading-5 text-muted-foreground">
                        {error ?? unit.unitId}
                    </div>
                    <button
                        type="button"
                        className="mt-3 inline-flex h-7 items-center gap-1 rounded border border-border px-2 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={() => { void refresh(); }}
                    >
                        <RefreshCw size={12} />
                        {t("common.retry")}
                    </button>
                </div>
            </div>
        );
    }

    const definition = snapshot.definitions.get(unit.unitId);

    return (
        <div className={cn("h-full min-h-0 overflow-auto bg-background", className)}>
            {definition ? (
                <DocContextProvider value={docValue}>
                    <ExecutionContextProvider value={executionValue}>
                        <PluginErrorBoundary
                            pluginName={definition.pluginName}
                            definitionId={unit.unitId}
                        >
                            <div className="p-2">
                                <PluginBlock definitionId={unit.unitId} attrs={{}} mode="block" />
                            </div>
                        </PluginErrorBoundary>
                    </ExecutionContextProvider>
                </DocContextProvider>
            ) : (
                <div className="flex h-full items-center justify-center p-4 text-xs text-muted-foreground">
                    {t("definition.notInstalled")}
                </div>
            )}
        </div>
    );
}
